import { apiClient, ApiResponse } from './apiClient';
import { DEBUG } from '@/config/api-config';
import { Property } from './propertyService';

// API Response Types based on the real API
export interface ApiBookingUser {
  id: string;
  name: string;
  email: string;
  phone: string;
}

export interface ApiBookingProperty {
  id: number;
  name: string;
}

export interface PaymentMethod {
  id: string;
  provider_name: string;
  provider_code: string;
  image_url: string;
  merchant_id: string;
  public_key: string;
  secret_key: string;
  callback_url: string;
  status: string;
  display_order: number;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiBooking {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  status: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'completed';
  payment_status: 'pending' | 'paid' | 'refunded';
  couponCode: string | null;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  property_id: number;
  total_price: string;
  deposit_amount: string;
  deposit_paid: string;
  remaining_amount: string;
  payment_method: string;
  payment_reference: string | null;
  check_in: string;
  check_out: string;
  guest_count: number;
  special_requests: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  couponId: string | null;
  User: ApiBookingUser;
  Property: ApiBookingProperty;
}

export interface ApiBookingsResponse {
  success: boolean;
  data: {
    bookings: ApiBooking[];
    pagination: {
      total: number;
      total_pages: number;
      current_page: number;
      per_page: number;
    };
  };
}

// Legacy interfaces for backward compatibility
export interface BookingCustomer {
  id: number;
  name: string;
  email: string;
  phone: string;
  language: 'en' | 'ar';
}

export interface PaymentSummary {
  full_amount: number;
  deposit_amount: number;
  deposit_paid: number;
  remaining_amount: number;
  discount_applied?: number;
  coupon_used?: string;
  payment_method: string;
}

export interface Booking {
  id: string; // Changed from number to string to match API
  property_id: number;
  customer_id: string; // Changed from number to string
  check_in: string;
  check_out: string;
  guest_count: number;
  status: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'completed';
  payment_status: 'pending' | 'paid' | 'refunded'; // Updated to match API
  payment_method: 'visa' | 'mastercard' | 'mada' | 'apple_pay' | 'stc_pay' | 'neolep';
  special_requests?: string;
  total_amount: number;
  deposit_amount: number;
  remaining_amount: number;
  coupon_code?: string;
  discount_applied?: number;
  booking_reference: string;
  
  // Relations
  property?: Property;
  customer?: BookingCustomer;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export interface CreatePropertyBookingRequest {
  property_id: number;
  check_in: string; // ISO date string
  check_out: string; // ISO date string
  guest_count: number;
  payment_method: string; // e.g., 'TABBY', 'visa', 'mastercard', etc.
  payment_method_code?: string; // Provider code (e.g., 'TABBY')
  special_requests?: string;
  couponCode?: string;
}

export interface PaymentInfo {
  sessionId: string;
  orderId: string;
  paymentUrl: string;
  redirectRequired: boolean;
  payment_scope?: string;
}

export interface NextSteps {
  message: string;
  action: string;
  payment_url: string;
}

export interface CreatePropertyBookingResponse {
  booking: ApiBooking | null;
  payment: PaymentInfo;
  sms_sent?: boolean;
  phone?: string;
  // Optional fields that may be present in some responses
  payment_summary?: {
    total_amount: number;
    deposit_amount: string;
    remaining_amount: number | string;
    payment_method: string;
    payment_status: string;
    booking_status: string;
  };
  next_steps?: NextSteps;
  message?: string;
}

// Legacy room booking interfaces (for backward compatibility)
export interface Room {
  id: string;
  name: string;
  price: number;
  description: string;
  capacity: number;
  amenities: string[];
  images: string[];
  available: boolean;
}

export interface CreateRoomBookingRequest {
  roomId: string;
  startDate: string;
  endDate: string;
  paymentMethodId: string;
  couponCode?: string;
}

export interface BookingQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  payment_status?: string;
  property_id?: number;
  customer_id?: number;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
  // New API parameters as specified by user
  userId?: string;
  propertyId?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  userSearch?: string;
  propertySearch?: string;
}

export interface BookingStats {
  total_bookings: number;
  confirmed_bookings: number;
  pending_bookings: number;
  completed_bookings: number;
  cancelled_bookings: number;
  canceled_bookings: number; // Alternative spelling for compatibility
  total_revenue: number;
  pending_payments: number;
  pending_deposits: number; // Alternative name for compatibility
  paid_bookings: number;
  average_booking_value: number;
  occupancy_rate: number;
  popular_properties: any[];
  monthly_revenue: any[];
  booking_trends: any[];
}

// Coupon validation
export interface CouponValidationRequest {
  couponCode: string;
  orderAmount: number;
}

export interface CouponValidationResponse {
  valid: boolean;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  discount_amount: number;
  final_amount: number;
  message: string;
  coupon_details?: {
    id: number;
    code: string;
    description: string;
    min_order_amount?: number;
    max_discount?: number;
    expires_at?: string;
  };
}

// Payment completion
export interface CompletePaymentRequest {
  booking_id: number;
  payment_method: string;
  payment_reference?: string;
  amount: number;
}

// Booking Service Class
// Helper function to transform API booking to legacy format
function transformApiBookingToLegacy(apiBooking: ApiBooking): Booking {
  return {
    id: apiBooking.id,
    property_id: apiBooking.property_id,
    customer_id: apiBooking.userId,
    check_in: apiBooking.check_in,
    check_out: apiBooking.check_out,
    guest_count: apiBooking.guest_count,
    status: apiBooking.status,
    payment_status: apiBooking.payment_status,
    payment_method: apiBooking.payment_method as 'visa' | 'mastercard' | 'mada' | 'apple_pay' | 'stc_pay' | 'neolep',
    special_requests: apiBooking.special_requests || undefined,
    total_amount: apiBooking.finalAmount,
    deposit_amount: parseFloat(apiBooking.deposit_amount),
    remaining_amount: parseFloat(apiBooking.remaining_amount),
    coupon_code: apiBooking.couponCode || undefined,
    discount_applied: apiBooking.discountAmount,
    booking_reference: apiBooking.id, // Use ID as reference
    property: apiBooking.Property ? {
      id: apiBooking.Property.id,
      name: apiBooking.Property.name,
      // Add other property fields as needed
    } as Property : undefined,
    customer: {
      id: parseInt(apiBooking.User.id) || 0,
      name: apiBooking.User.name,
      email: apiBooking.User.email,
      phone: apiBooking.User.phone,
      language: 'en' // Default language
    },
    createdAt: apiBooking.createdAt,
    updatedAt: apiBooking.updatedAt,
  };
}

// ==================== ADMIN BOOKINGS (UNIFIED ENTRY POINT) ====================

// Admin booking request types matching /admin/bookings contract
export interface AdminBookingRequestBase {
  property_id: number;
  check_in: string; // ISO 8601
  check_out: string; // ISO 8601
  guest_count: number;
  payment_method?: string;
  payment_method_code?: string;
  payment_scope?: 'deposit' | 'full';
  special_requests?: string;
  couponCode?: string | null;
}

// Type 1: New / unregistered user (no userId, requires phone)
export interface AdminBookingRequestNewUser extends AdminBookingRequestBase {
  phone: string;
  customerName?: string;
  userId?: never;
}

// Type 2: Existing user (has userId, no phone required)
export interface AdminBookingRequestExistingUser extends AdminBookingRequestBase {
  userId: string;
  phone?: never;
  customerName?: never;
}

export type AdminBookingRequest =
  | AdminBookingRequestNewUser
  | AdminBookingRequestExistingUser;

// Response data when payment session is created
export interface AdminBookingPaymentData {
  booking: null;
  payment: PaymentInfo;
  sms_sent?: boolean;
  phone?: string;
}

// Response data when booking is created directly (no payment method)
// We reuse ApiBooking for this case.
export type AdminBookingData = AdminBookingPaymentData | ApiBooking;

export class BookingService {
  // Create booking via /admin/bookings (handles both new and existing users)
  async createAdminBooking(
    data: AdminBookingRequest
  ): Promise<ApiResponse<AdminBookingData>> {
    if (DEBUG) {
      console.log('👨‍💼 Creating admin booking via /admin/bookings:', data);
    }

    return apiClient.post<AdminBookingData>('/admin/bookings', data);
  }

  // Confirm payment success after Tabby redirect
  async confirmPaymentSuccess(params: {
    sessionId: string;
    status: string;
    productOptionId?: string;
  }): Promise<ApiResponse<any>> {
    if (DEBUG) {
      console.log('✅ Confirming payment success:', params);
    }
    
    const queryParams = new URLSearchParams({
      sessionId: params.sessionId,
      status: params.status,
    });
    
    if (params.productOptionId) {
      queryParams.append('productOptionId', params.productOptionId);
    }
    
    return apiClient.get(`/api/payment/success?${queryParams.toString()}`);
  }

  // Send SMS to user via admin bookings API
  async sendSmsToUser(data: {
    userId: string;
    phone: string;
    property_id: number;
    check_in: string;
    check_out: string;
    guest_count: number;
    paymentUrl?: string;
  }): Promise<ApiResponse<{ sms_sent: boolean; phone?: string; message?: string }>> {
    if (DEBUG) {
      console.log('📱 Sending SMS via /admin/bookings:', data);
    }
    
    try {
      // Call /admin/bookings with booking data and SMS notification flag
      const response = await apiClient.post(`/admin/bookings`, {
        userId: data.userId,
        property_id: data.property_id,
        check_in: data.check_in,
        check_out: data.check_out,
        guest_count: data.guest_count,
        send_sms: true,
        notify: true,
        notify_sms: true,
        phone: data.phone,
        payment_url: data.paymentUrl
      });
      
      if (DEBUG) {
        console.log('📱 SMS response from /admin/bookings:', response);
      }
      
      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('📱 Error sending SMS:', error);
      }
      throw error;
    }
  }

  // Get booking by ID
  async getBooking(id: number): Promise<ApiResponse<Booking>> {
    if (DEBUG) {
      console.log('🏛️ Fetching booking:', id);
    }
    return apiClient.get<Booking>(`/user/booking/${id}`);
  }

  // Get booking details by ID (Admin)
  async getBookingDetails(id: string): Promise<ApiResponse<any>> {
    if (DEBUG) {
      console.log('👨‍💼 Fetching booking details (admin):', id);
    }
    
    try {
      const response = await apiClient.get(`/admin/bookings/${id}`);
      
      if (DEBUG) {
        console.log('👨‍💼 Booking details response:', response);
      }
      
      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('👨‍💼 Error fetching booking details:', error);
      }
      throw error;
    }
  }

  // Change booking status (Admin)
  async changeBookingStatus(id: string, status: string): Promise<ApiResponse<any>> {
    if (DEBUG) {
      console.log('👨‍💼 Changing booking status (admin):', id, status);
    }
    
    try {
      const response = await apiClient.patch(`/admin/bookings/${id}/status`, { status });
      
      if (DEBUG) {
        console.log('👨‍💼 Status change response:', response);
      }
      
      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('👨‍💼 Error changing booking status:', error);
      }
      throw error;
    }
  }

  // Cancel booking (Admin)
  async cancelBookingAdmin(id: string, reason: string): Promise<ApiResponse<any>> {
    if (DEBUG) {
      console.log('👨‍💼 Cancelling booking (admin):', id, reason);
    }
    
    try {
      const response = await apiClient.patch(`/admin/bookings/${id}/cancel`, { reason });
      
      if (DEBUG) {
        console.log('👨‍💼 Cancellation response:', response);
      }
      
      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('👨‍💼 Error cancelling booking:', error);
      }
      throw error;
    }
  }

  // Get user's bookings
  async getUserBookings(params?: BookingQueryParams): Promise<ApiResponse<{
    bookings: Booking[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>> {
    if (DEBUG) {
      console.log('🏛️ Fetching user bookings with params:', params);
    }
    
    const queryString = params ? '?' + new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString() : '';
    
    return apiClient.get<{
      bookings: Booking[];
      pagination: any;
    }>(`/user/bookings${queryString}`);
  }

  // Update booking
  async updateBooking(id: number, data: Partial<CreatePropertyBookingRequest>): Promise<ApiResponse<Booking>> {
    if (DEBUG) {
      console.log('🏛️ Updating booking:', id, data);
    }
    return apiClient.patch<Booking>(`/user/booking/${id}`, data);
  }

  // Cancel booking
  async cancelBooking(id: number, reason?: string): Promise<ApiResponse<Booking>> {
    if (DEBUG) {
      console.log('🏛️ Cancelling booking:', id, reason);
    }
    return apiClient.patch<Booking>(`/user/booking/${id}/cancel`, { reason });
  }

  // Complete remaining payment
  async completePayment(data: CompletePaymentRequest): Promise<ApiResponse<{
    booking: Booking;
    payment_summary: PaymentSummary;
    message: string;
  }>> {
    if (DEBUG) {
      console.log('💳 Completing payment for booking:', data.booking_id);
    }
    return apiClient.post<{
      booking: Booking;
      payment_summary: PaymentSummary;
      message: string;
    }>('/user/complete-payment', data);
  }

  // ==================== LEGACY ROOM BOOKING (Backward Compatibility) ====================

  // List rooms (Public - Legacy)
  async getRooms(): Promise<ApiResponse<Room[]>> {
    if (DEBUG) {
      console.log('🏨 Fetching rooms (legacy)');
    }
    return apiClient.get<Room[]>('/user/rooms');
  }

  // Create room booking (Legacy)
  async createRoomBooking(data: CreateRoomBookingRequest): Promise<ApiResponse<Booking>> {
    if (DEBUG) {
      console.log('🏨 Creating room booking (legacy):', data);
    }
    return apiClient.post<Booking>('/user/booking', data);
  }

  // ==================== COUPON VALIDATION ====================

  // Validate coupon before purchase
  async validateCoupon(data: CouponValidationRequest): Promise<ApiResponse<CouponValidationResponse>> {
    if (DEBUG) {
      console.log('🎫 Validating coupon:', data.couponCode, 'for amount:', data.orderAmount);
    }
    return apiClient.post<CouponValidationResponse>('/user/validate-coupon', data);
  }

  // ==================== DROPDOWN DATA FETCHING ====================
  
  // Get users for dropdown (Admin/Staff only)
  async getUsers(searchQuery: string = ''): Promise<Array<{id: string, name: string}>> {
    console.log('🔧 BookingService.getUsers called with search:', searchQuery);
    
    try {
      const queryString = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const url = `/admin/users${queryString}`;
      console.log('🔧 Making API call to:', url);
      
      const response = await apiClient.get<Array<{id: string, name: string}>>(url);
      console.log('🔧 API response:', response);
      
      if (response.success && response.data) {
        console.log('✅ API success, returning data:', response.data);
        
        // Handle different response structures
        let users = response.data;
        
        // If response.data is an object with a users array
        if (users && typeof users === 'object' && !Array.isArray(users)) {
          if ((users as any).users && Array.isArray((users as any).users)) {
            console.log('📋 Found users in response.data.users');
            users = (users as any).users;
          } else if ((users as any).data && Array.isArray((users as any).data)) {
            console.log('📋 Found users in response.data.data');
            users = (users as any).data;
          }
        }
        
        // Ensure it's an array and transform to expected format
        if (Array.isArray(users)) {
          console.log('📋 Processing users array:', users.length, 'items');
          return users.map((user: any) => ({
            id: String(user.id || user._id || ''),
            name: user.name || user.username || user.email || `User ${user.id}` || 'Unknown User'
          }));
        } else {
          console.log('⚠️ Users data is not an array:', typeof users, users);
        }
      }
      
      console.log('⚠️ API failed or no data, using mock data');
      // Fallback to mock data if API fails
      const mockUsers = [
        { id: '1', name: 'أحمد محمد' },
        { id: '2', name: 'فاطمة علي' },
        { id: '3', name: 'محمد سعد' },
        { id: '4', name: 'نورا أحمد' },
        { id: '5', name: 'عبدالله خالد' }
      ];
      
      const filteredUsers = searchQuery 
        ? mockUsers.filter(user => 
            user.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : mockUsers;
      
      console.log('📋 Returning mock users:', filteredUsers);
      return filteredUsers;
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      
      // Return mock data on error
      const mockUsers = [
        { id: '1', name: 'أحمد محمد' },
        { id: '2', name: 'فاطمة علي' },
        { id: '3', name: 'محمد سعد' },
        { id: '4', name: 'نورا أحمد' },
        { id: '5', name: 'عبدالله خالد' }
      ];
      
      const filteredUsers = searchQuery 
        ? mockUsers.filter(user => 
            user.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : mockUsers;
      
      console.log('📋 Returning mock users after error:', filteredUsers);
      return filteredUsers;
    }
  }

  // Get properties for dropdown (Admin/Staff only)
  async getProperties(searchQuery: string = ''): Promise<Array<{id: string, name: string}>> {
    console.log('🔧 BookingService.getProperties called with search:', searchQuery);
    
    try {
      const queryString = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const url = `/properties${queryString}`;
      console.log('🔧 Making API call to:', url);
      
      const response = await apiClient.get<Array<{id: string, name: string}>>(url);
      console.log('🔧 API response:', response);
      
      if (response.success && response.data) {
        console.log('✅ API success, returning data:', response.data);
        
        // Handle different response structures
        let properties = response.data;
        
        // If response.data is an object with a properties array
        if (properties && typeof properties === 'object' && !Array.isArray(properties)) {
          if ((properties as any).properties && Array.isArray((properties as any).properties)) {
            console.log('📋 Found properties in response.data.properties');
            properties = (properties as any).properties;
          } else if ((properties as any).data && Array.isArray((properties as any).data)) {
            console.log('📋 Found properties in response.data.data');
            properties = (properties as any).data;
          }
        }
        
        // Ensure it's an array and transform to expected format
        if (Array.isArray(properties)) {
          console.log('📋 Processing properties array:', properties.length, 'items');
          return properties.map((property: any) => ({
            id: String(property.id || property._id || ''),
            name: property.name || property.title || `Property ${property.id}` || 'Unknown Property'
          }));
        } else {
          console.log('⚠️ Properties data is not an array:', typeof properties, properties);
        }
      }
      
      console.log('⚠️ API failed or no data, using mock data');
      // Fallback to mock data if API fails
      const mockProperties = [
        { id: '1', name: 'فيلا الرياض الفاخرة' },
        { id: '2', name: 'شقة جدة المطلة على البحر' },
        { id: '3', name: 'استراحة الدمام العائلية' },
        { id: '4', name: 'قصر مكة المكرمة' },
        { id: '5', name: 'شاليه أبها الجبلي' }
      ];
      
      const filteredProperties = searchQuery 
        ? mockProperties.filter(property => 
            property.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : mockProperties;
      
      console.log('📋 Returning mock properties:', filteredProperties);
      return filteredProperties;
    } catch (error) {
      console.error('❌ Error fetching properties:', error);
      
      // Return mock data on error
      const mockProperties = [
        { id: '1', name: 'فيلا الرياض الفاخرة' },
        { id: '2', name: 'شقة جدة المطلة على البحر' },
        { id: '3', name: 'استراحة الدمام العائلية' },
        { id: '4', name: 'قصر مكة المكرمة' },
        { id: '5', name: 'شاليه أبها الجبلي' }
      ];
      
      const filteredProperties = searchQuery 
        ? mockProperties.filter(property => 
            property.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : mockProperties;
      
      console.log('📋 Returning mock properties after error:', filteredProperties);
      return filteredProperties;
    }
  }

  // ==================== PAYMENT METHODS ====================

  // Get payment methods
  async getPaymentMethods(): Promise<ApiResponse<{ payment_methods: PaymentMethod[], total: number, current_page: number, total_pages: number }>> {
    if (DEBUG) {
      console.log('💳 Fetching payment methods...');
    }
    
    try {
      const response = await apiClient.get<{ payment_methods: PaymentMethod[], total: number, current_page: number, total_pages: number }>('/admin/payment-methods');
      
      if (DEBUG) {
        console.log('💳 Payment methods response:', response);
      }
      
      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('💳 Error fetching payment methods:', error);
      }
      throw error;
    }
  }

// (Other admin booking management methods stay unchanged below)

  // View bookings (used by BookingList component)
  async viewBookings(request: {
    filter?: any;
    page?: number;
    limit?: number;
  }): Promise<{
    data: Booking[];
    pagination: {
      total_pages: number;
      current_page: number;
      total: number;
    };
  }> {
    if (DEBUG) {
      console.log('👁️ Viewing bookings with request:', request);
    }
    
    // Build query parameters from the request
    const queryParams: BookingQueryParams = {};
    
    if (request.page) {
      queryParams.page = request.page;
    }
    
    if (request.limit) {
      queryParams.limit = request.limit;
    }
    
    // Add filter parameters if they exist
    if (request.filter) {
      if (request.filter.userId) {
        queryParams.userId = request.filter.userId;
      }
      if (request.filter.propertyId) {
        queryParams.propertyId = request.filter.propertyId;
      }
      if (request.filter.start_date) {
        queryParams.start_date = request.filter.start_date;
      }
      if (request.filter.end_date) {
        queryParams.end_date = request.filter.end_date;
      }
      if (request.filter.status) {
        queryParams.status = request.filter.status;
      }
      if (request.filter.search) {
        queryParams.search = request.filter.search;
      }
      if (request.filter.userSearch) {
        queryParams.userSearch = request.filter.userSearch;
      }
      if (request.filter.propertySearch) {
        queryParams.propertySearch = request.filter.propertySearch;
      }
    }
    
    // Use getAllBookings method which calls /admin/bookings with parameters
    const response = await this.getAllBookings(queryParams);
    
    if (response.success && response.data) {
      return {
        data: response.data.bookings,
        pagination: {
          total_pages: response.data.pagination.totalPages,
          current_page: response.data.pagination.page,
          total: response.data.pagination.total,
        }
      };
    }
    
    throw new Error('Failed to fetch bookings');
  }

  // List all bookings (Admin/Staff)
  async getAllBookings(params?: BookingQueryParams): Promise<ApiResponse<{
    bookings: Booking[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>> {
    if (DEBUG) {
      console.log('👨‍💼 Fetching all bookings (admin) with params:', params);
    }
    
    const queryString = params ? '?' + new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    ).toString() : '';
    
    try {
      // Call the real API endpoint
      const response = await apiClient.get<ApiBookingsResponse>(`/admin/bookings${queryString}`);
      
      if (DEBUG) {
        console.log('👨‍💼 Raw API response:', response);
      }
      
      // Transform the API response to match expected format
      if (response.success && response.data) {
        // Handle different response structures
        let bookings: ApiBooking[] = [];
        let pagination: any = {};
        
        if (Array.isArray(response.data)) {
          // Direct array response
          bookings = response.data;
          pagination = { current_page: 1, per_page: bookings.length, total: bookings.length, total_pages: 1 };
        } else if (response.data.bookings && Array.isArray(response.data.bookings)) {
          // Nested bookings array
          bookings = response.data.bookings;
          pagination = response.data.pagination || { current_page: 1, per_page: bookings.length, total: bookings.length, total_pages: 1 };
        } else {
          console.warn('Unexpected API response structure:', response.data);
          return response as any;
        }
        
        const transformedBookings = bookings.map(transformApiBookingToLegacy);
        
        const transformedResponse = {
          success: true,
          data: {
            bookings: transformedBookings,
            pagination: {
              page: pagination.current_page,
              limit: pagination.per_page,
              total: pagination.total,
              totalPages: pagination.total_pages,
            }
          },
          message: response.message || 'Bookings fetched successfully'
        };
        
        if (DEBUG) {
          console.log('👨‍💼 Transformed response:', transformedResponse);
        }
        
        return transformedResponse;
      }
      
      return response as any;
    } catch (error) {
      if (DEBUG) {
        console.error('👨‍💼 Error fetching bookings:', error);
      }
      throw error;
    }
  }

  // Update booking status (Admin/Staff)
  async updateBookingStatus(
    id: number, 
    status: Booking['status'],
    notes?: string
  ): Promise<ApiResponse<Booking>> {
    if (DEBUG) {
      console.log('👨‍💼 Updating booking status:', id, 'to', status);
    }
    return apiClient.patch<Booking>(`/admin/bookings/${id}/status`, { status, notes });
  }

  // Update payment status (Admin/Staff)
  async updatePaymentStatus(
    id: number, 
    payment_status: Booking['payment_status'],
    notes?: string
  ): Promise<ApiResponse<Booking>> {
    if (DEBUG) {
      console.log('👨‍💼 Updating payment status:', id, 'to', payment_status);
    }
    return apiClient.patch<Booking>(`/admin/bookings/${id}/payment-status`, { payment_status, notes });
  }

  // Get booking statistics (Admin/Staff) - Calculate from bookings data
  async getBookingStats(
    date_from?: string,
    date_to?: string
  ): Promise<ApiResponse<BookingStats>> {
    if (DEBUG) {
      console.log('📊 Calculating booking statistics from /admin/bookings data');
    }
    
    try {
      // Get all bookings from /admin/bookings and calculate stats
      const bookingsResponse = await this.getAllBookings();
      
      if (!bookingsResponse.success || !bookingsResponse.data) {
        throw new Error('Failed to fetch bookings for stats calculation');
      }
      
      const bookings = bookingsResponse.data.bookings;
      
      // Filter by date range if provided
      let filteredBookings = bookings;
      if (date_from || date_to) {
        filteredBookings = bookings.filter(booking => {
          const bookingDate = new Date(booking.createdAt);
          const fromDate = date_from ? new Date(date_from) : null;
          const toDate = date_to ? new Date(date_to) : null;
          
          if (fromDate && bookingDate < fromDate) return false;
          if (toDate && bookingDate > toDate) return false;
          return true;
        });
      }
      
      // Calculate statistics from filtered bookings
      const cancelledCount = filteredBookings.filter(b => b.status === 'cancelled').length;
      const pendingPaymentsCount = filteredBookings.filter(b => b.payment_status === 'pending').length;
      
      const stats: BookingStats = {
        total_bookings: filteredBookings.length,
        confirmed_bookings: filteredBookings.filter(b => b.status === 'confirmed').length,
        pending_bookings: filteredBookings.filter(b => b.status === 'pending').length,
        completed_bookings: filteredBookings.filter(b => b.status === 'completed').length,
        cancelled_bookings: cancelledCount,
        canceled_bookings: cancelledCount, // Alternative spelling
        total_revenue: filteredBookings.reduce((sum, b) => sum + b.total_amount, 0),
        pending_payments: pendingPaymentsCount,
        pending_deposits: pendingPaymentsCount, // Alternative name
        paid_bookings: filteredBookings.filter(b => b.payment_status === 'paid').length,
        average_booking_value: filteredBookings.length > 0 ? 
          filteredBookings.reduce((sum, b) => sum + b.total_amount, 0) / filteredBookings.length : 0,
        occupancy_rate: 0, // Would need property data to calculate properly
        popular_properties: [], // Would need to aggregate by property
        monthly_revenue: [], // Would need to group by month
        booking_trends: [] // Would need historical data
      };
      
      if (DEBUG) {
        console.log('📊 Calculated stats from bookings:', stats);
      }
      
      return {
        success: true,
        data: stats,
        message: 'Statistics calculated successfully from bookings data'
      };
    } catch (error) {
      if (DEBUG) {
        console.error('📊 Error calculating booking stats:', error);
      }
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  // Calculate booking duration in days
  calculateDuration(checkIn: string, checkOut: string): number {
    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Calculate total amount
  calculateTotalAmount(property: any, checkIn: string, checkOut: string): number {
    const duration = this.calculateDuration(checkIn, checkOut);
    return property.full_price * duration;
  }

  // Calculate deposit amount
  calculateDepositAmount(property: any, totalAmount: number): number {
    if (property.deposit_type === 'fixed') {
      return property.deposit_value;
    } else {
      return Math.round((totalAmount * property.deposit_value) / 100);
    }
  }

  // Calculate remaining amount after deposit
  calculateRemainingAmount(totalAmount: number, depositAmount: number): number {
    return totalAmount - depositAmount;
  }

  // Format booking status for display
  formatBookingStatus(status: Booking['status']): { label: string; color: string } {
    const statusMap = {
      pending: { label: 'Pending', color: 'bg-yellow-500' },
      confirmed: { label: 'Confirmed', color: 'bg-blue-500' },
      checked_in: { label: 'Checked In', color: 'bg-green-500' },
      checked_out: { label: 'Checked Out', color: 'bg-gray-500' },
      cancelled: { label: 'Cancelled', color: 'bg-red-500' },
      completed: { label: 'Completed', color: 'bg-green-600' }
    };
    
    return statusMap[status] || { label: status, color: 'bg-gray-400' };
  }

  // Format payment status for display
  formatPaymentStatus(status: Booking['payment_status']): { label: string; color: string } {
    const statusMap = {
      pending: { label: 'Pending', color: 'bg-yellow-500' },
      paid: { label: 'Paid', color: 'bg-green-500' },
      deposit_paid: { label: 'Deposit Paid', color: 'bg-blue-500' },
      fully_paid: { label: 'Fully Paid', color: 'bg-green-500' },
      refunded: { label: 'Refunded', color: 'bg-red-500' }
    };
    
    return statusMap[status] || { label: status, color: 'bg-gray-400' };
  }

  // Generate booking reference
  generateBookingReference(): string {
    const prefix = 'BK';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}${timestamp}${random}`;
  }

  // Check if booking can be cancelled
  canCancelBooking(booking: Booking): boolean {
    const now = new Date();
    const checkInDate = new Date(booking.check_in);
    const hoursUntilCheckIn = (checkInDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    // Can cancel if more than 24 hours before check-in and status allows
    return hoursUntilCheckIn > 24 && ['pending', 'confirmed'].includes(booking.status);
  }

  // Check if remaining payment is due
  isPaymentDue(booking: Booking): boolean {
    const now = new Date();
    const checkInDate = new Date(booking.check_in);
    const hoursUntilCheckIn = (checkInDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    // Payment due if within 48 hours of check-in and not fully paid
    return hoursUntilCheckIn <= 48 && 
           booking.payment_status === 'pending' && 
           booking.remaining_amount > 0;
  }

  // Refund booking (Admin)
  async refundBooking(id: string, reason?: string): Promise<ApiResponse<any>> {
    if (DEBUG) {
      console.log('💰 Refunding booking (admin):', id, reason);
    }
    
    try {
      const response = await apiClient.patch(`/admin/bookings/${id}/refund`, { 
        reason: reason || 'Refund requested by admin' 
      });
      
      if (DEBUG) {
        console.log('💰 Refund response:', response);
      }
      
      return response;
    } catch (error) {
      if (DEBUG) {
        console.error('💰 Error refunding booking:', error);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const bookingService = new BookingService();
export default bookingService;